import { useEffect, useId, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Loader2, Plus } from 'lucide-react'

import { Button, Checkbox, useToast } from '../../radix'
import { ListNameField } from './ListNameField'
import { useFavouriteLists } from '../../../auth/hooks/useFavouriteLists'
import { itemCountLabel, saveListName } from '../../../utils/favouriteLists'
import type { FavouriteList } from '../../../types'
import type { SaveToListTarget } from './saveToListContext'

interface SaveToListSheetProps {
  target: SaveToListTarget
  onClose: () => void
}

/**
 * "Save to list" bottom sheet (DEV-51010 AC2–AC4, AC14, AC15): the product
 * name, then every list with a tick against the ones it's already in.
 *
 * - Ticking or unticking applies straight away; the count beside the list
 *   moves at once. Unticking only takes it out of that list.
 * - "New list" swaps for a name field. A valid name creates the list with
 *   this product in it, shown ticked at the top.
 * - Rows keep their order while the sheet is open, so they don't jump as
 *   you tick.
 * - The list scrolls inside the sheet; the header and "Close" stay put.
 *   "Close", not "Done": ticks are already saved, so the button only
 *   dismisses the sheet (product decision).
 * - Radix traps focus and closes on Escape (Escape in the name field closes
 *   just the field). The slide-up is skipped under reduced motion.
 */
export function SaveToListSheet({ target, onClose }: SaveToListSheetProps) {
  const { lists, isLoading, isError, refetch, setInList, createList } = useFavouriteLists()
  const { addToast } = useToast()
  const nameId = useId()

  // Frozen on first load; lists created here are put on top.
  const [order, setOrder] = useState<string[] | null>(null)
  if (order === null && !isLoading && !isError) {
    setOrder(lists.map((list) => list.id))
  }
  const shown = (order ?? [])
    .map((id) => lists.find((list) => list.id === id))
    .filter((list): list is FavouriteList => list !== undefined)

  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [status, setStatus] = useState('')
  const nameInputRef = useRef<HTMLInputElement>(null)
  const newListButtonRef = useRef<HTMLButtonElement>(null)
  const hasOpenedForm = useRef(false)

  // Keep focus inside the sheet as the "New list" row swaps between the
  // button and the form.
  useEffect(() => {
    if (naming) {
      hasOpenedForm.current = true
      nameInputRef.current?.focus()
    } else if (hasOpenedForm.current) {
      newListButtonRef.current?.focus()
    }
  }, [naming])

  const closeForm = () => {
    setNaming(false)
    setName('')
    setError(null)
  }

  const submitNewList = async () => {
    if (creating) return
    setCreating(true)
    const result = await saveListName(name, lists, (value) =>
      createList(value, target.favouriteId),
    )
    setCreating(false)
    if (!result.ok) {
      setError(result.error)
      nameInputRef.current?.focus()
      return
    }
    setOrder((previous) => [result.value.id, ...(previous ?? [])])
    setStatus(`Created ${result.value.name} and added this item`)
    closeForm()
  }

  const toggle = (list: FavouriteList, included: boolean) => {
    setInList({ listId: list.id, favouriteId: target.favouriteId, included }).catch(() => {
      addToast(`Couldn't update ${list.name}. Try again.`, 'error')
    })
  }

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          onEscapeKeyDown={(event) => {
            if (!naming) return
            event.preventDefault()
            closeForm()
          }}
          className="animate-sheet-enter fixed inset-x-0 bottom-0 z-[51] mx-auto flex max-h-[85dvh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-[0_-10px_28px_rgba(32,26,46,0.2)] focus:outline-none"
        >
          <div className="border-b border-gray-100 px-4 pb-3 pt-2">
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300" aria-hidden="true" />
            <Dialog.Title className="text-lg font-bold text-gray-900">Save to list</Dialog.Title>
            <Dialog.Description className="truncate text-sm text-gray-500">
              {target.productName}
            </Dialog.Description>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2">
            {naming ? (
              <form
                noValidate
                className="rounded-xl bg-gray-50 p-3"
                onSubmit={(event) => {
                  event.preventDefault()
                  submitNewList()
                }}
              >
                <ListNameField
                  ref={nameInputRef}
                  id={nameId}
                  value={name}
                  onChange={(value) => {
                    setName(value)
                    setError(null)
                  }}
                  error={error}
                />
                <div className="mt-3 flex gap-2">
                  <Button type="submit" size="sm" className="h-11 flex-1" disabled={creating}>
                    {creating && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                    Create list
                  </Button>
                  <Button type="button" variant="neutral" size="sm" className="h-11" onClick={closeForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <button
                ref={newListButtonRef}
                type="button"
                onClick={() => setNaming(true)}
                // Not until the lists have loaded: the row order is frozen on
                // first load, and duplicate names can't be checked before it.
                disabled={isLoading || isError}
                className="flex min-h-12 w-full items-center gap-3 rounded-xl px-2 text-left font-semibold text-gray-900 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-[6px] bg-brand-primary-tint text-brand-primary">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </span>
                New list
              </button>
            )}

            {isLoading ? (
              <div className="mt-1 space-y-1" aria-busy="true" aria-label="Loading lists">
                {[0, 1, 2].map((row) => (
                  <div key={row} className="h-12 rounded-xl bg-gray-100 motion-safe:animate-pulse" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex items-center justify-between gap-3 px-2 py-2 text-sm text-gray-600">
                <span>Couldn&rsquo;t load your lists.</span>
                <Button type="button" variant="neutral" size="sm" className="h-11" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            ) : shown.length === 0 ? (
              !naming && (
                <p className="px-2 pb-2 pt-1 text-sm text-gray-500">
                  No lists yet. Make one to keep a job&rsquo;s favourites together.
                </p>
              )
            ) : (
              <ul className="mt-1">
                {shown.map((list) => (
                  <li key={list.id}>
                    <label className="flex min-h-12 cursor-pointer items-center gap-3 rounded-xl px-2 hover:bg-gray-50">
                      <Checkbox
                        checked={list.favouriteIds.includes(target.favouriteId)}
                        onChange={(event) => toggle(list, event.target.checked)}
                      />
                      <span className="min-w-0 flex-1 truncate text-gray-900">{list.name}</span>
                      <span className="flex-shrink-0 text-sm text-gray-500">
                        {itemCountLabel(list.favouriteIds.length)}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-gray-100 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
            <Button type="button" variant="neutral" className="w-full" onClick={onClose}>
              Close
            </Button>
          </div>

          <p role="status" className="sr-only">
            {status}
          </p>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
